//
// Copyright (c) 2023 Emplifi, Inc.
//

import type {
  ModelElement,
  ModelNode,
  ModelPosition,
  ModelWriter,
  UpcastConversionApi,
  UpcastConversionData,
  ViewConsumable,
  ViewElement,
} from "ckeditor5";
import { MediaEmbedEditing, Plugin } from "ckeditor5";

export default class CkEditor4MediaEmbed extends Plugin {
  // noinspection JSUnusedGlobalSymbols
  static get pluginName() {
    return "CkEditor4MediaEmbed";
  }

  // noinspection JSUnusedGlobalSymbols
  static get requires() {
    return [MediaEmbedEditing];
  }

  init() {
    this._registerConverters();
  }

  private _registerConverters() {
    const editor = this.editor;

    // the following function was taken from the tutorial
    // https://ckeditor.com/docs/ckeditor5/latest/framework/deep-dive/conversion/upcast.html#converting-structures
    editor.conversion.for("upcast").add((dispatcher) => {
      dispatcher.on(
        "element:div",
        (_evt, data: UpcastConversionData<ViewElement>, conversionApi: UpcastConversionApi) => {
          const { consumable, writer, safeInsert, updateConversionResult } = conversionApi;
          const { viewItem } = data;

          if (!consumable.test(viewItem, { name: true })) {
            return;
          }

          if (viewItem.childCount !== 1) {
            return;
          }

          const firstChildItem = viewItem.getChild(0);

          if (!firstChildItem || !firstChildItem.is("element", "iframe")) {
            return;
          }

          const modelElement = this._convertIframe(
            data,
            firstChildItem,
            consumable,
            writer,
            safeInsert,
          );
          consumable.consume(viewItem, { name: true });
          if (!modelElement) {
            return;
          }

          updateConversionResult(modelElement, data);
        },
        { priority: "highest" },
      );
    });

    editor.conversion.for("upcast").add((dispatcher) => {
      dispatcher.on(
        "element:iframe",
        (_evt, data: UpcastConversionData<ViewElement>, conversionApi: UpcastConversionApi) => {
          const { consumable, writer, safeInsert, updateConversionResult } = conversionApi;
          const { viewItem } = data;
          const modelElement = this._convertIframe(data, viewItem, consumable, writer, safeInsert);
          if (!modelElement) {
            return;
          }
          updateConversionResult(modelElement, data);
        },
        { priority: "high" },
      );
    });
  }

  private _convertIframe(
    data: UpcastConversionData<ViewElement>,
    iframe: ViewElement,
    consumable: ViewConsumable,
    writer: ModelWriter,
    safeInsert: (_modelNode: ModelNode, _position: ModelPosition) => boolean,
  ): ModelElement | undefined {
    if (!consumable.test(iframe, { name: true })) {
      return;
    }

    const modelElement = writer.createElement("media");
    if (!this._isSupportedUrl(iframe.getAttribute("src"))) {
      return;
    }
    writer.setAttribute("url", iframe.getAttribute("src"), modelElement);
    writer.setAttribute("width", iframe.getAttribute("width"), modelElement);

    if (!safeInsert(modelElement, data.modelCursor)) {
      return;
    }

    consumable.consume(iframe, { name: true });
    return modelElement;
  }

  private _isSupportedUrl(url: string | undefined) {
    if (!url) {
      return false;
    }
    const mediaRegistry = this.editor.plugins.get(MediaEmbedEditing).registry;
    return mediaRegistry.hasMedia(url);
  }
}
